/**
 * Product Pack Editor V4.0 - Knowledge Brain UI
 * 
 * 7-Section Accordion:
 * 1. Product Identity
 * 2. Ingredient Intelligence  
 * 3. Benefit & Proof
 * 4. Social & Emotional
 * 5. Commercial
 * 6. Research & Provenance
 * 7. System & Versioning
 * 
 * Features:
 * - Section-level Save/Cancel bars
 * - Single-button AI flow (Hybrid/Saintifik/Emosi)
 * - Benefits chip editor with provenance
 * - Research study import (DOI/PDF)
 * - Mobile-first with iOS keyboard stability
 * - Toast notifications and timestamps
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import AmbassadorLinkingModal from './AmbassadorLinkingModal';
import { txt } from '../utils/bilingualText';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

// Helper text translations for ALL 7 sections
const FIELD_HELPERS = {
  // Section 1: Product Identity
  productName: {
    bm: 'Nama produk dalam bahasa yang anda selesa. Contoh: "Kopi Ala Kazim" atau "Ala Kazim Coffee"',
    urban: 'Nama product dalam language yang u comfortable. Contoh: "Kopi Ala Kazim" atau "Ala Kazim Coffee"',
    en: 'Product name in any language. Example: "Kopi Ala Kazim" or "Ala Kazim Coffee"'
  },
  description: {
    bm: 'Penerangan lengkap produk. Terangkan apa produk ini, untuk siapa, dan kenapa istimewa. AI akan guna untuk generate content yang menarik.',
    urban: 'Full description produk. Explain apa product ni, untuk siapa, dan why special. AI akan guna untuk generate engaging content.',
    en: 'Full product description. Explain what this product is, who it\'s for, and why it\'s special. AI will use this to generate engaging content.'
  },
  usp: {
    bm: 'Unique Selling Points - Apa yang buat produk ini berbeza dan istimewa? Contoh: "Resepi Ustaz Kazim yang terpercaya", "100% bahan semula jadi"',
    urban: 'USPs - What makes this product different dan special? Contoh: "Ustaz Kazim\'s trusted recipe", "100% natural ingredients"',
    en: 'Unique Selling Points - What makes this product different and special? Example: "Ustaz Kazim\'s trusted recipe", "100% natural ingredients"'
  },
  targetCustomer: {
    bm: 'Siapa target pelanggan? Contoh: "Dewasa 40+ dengan masalah gula darah", "Ibu mengandung yang nak supplement natural"',
    urban: 'Who is the target customer? Contoh: "Adults 40+ dengan blood sugar issues", "Pregnant moms yang nak natural supplements"',
    en: 'Who is the target customer? Example: "Adults 40+ with blood sugar concerns", "Pregnant mothers seeking natural supplements"'
  },
  shortDesc: {
    bm: 'Penerangan ringkas (1-2 ayat) untuk preview dan summary. Contoh: "Kopi herba khas untuk kawalan gula darah dengan Peria Katak"',
    urban: 'Short description (1-2 sentences) untuk preview dan summary. Example: "Herbal coffee specially untuk blood sugar control dengan Peria Katak"',
    en: 'Short description (1-2 sentences) for previews and summaries. Example: "Herbal coffee specially formulated for blood sugar control with Peria Katak"'
  },
  
  // Section 2: Ingredients
  ingredientName: {
    bm: 'Nama ramuan/bahan. Contoh: "Peria Katak", "Biji Kopi Arabica", "Extra Virgin Olive Oil"',
    urban: 'Nama ingredient/bahan. Example: "Peria Katak", "Arabica Coffee Beans", "Extra Virgin Olive Oil"',
    en: 'Ingredient name. Example: "Peria Katak", "Arabica Coffee Beans", "Extra Virgin Olive Oil"'
  },
  scientificName: {
    bm: 'Nama saintifik (optional). Contoh: "Momordica charantia" untuk Peria Katak. Guna untuk kredibiliti scientific.',
    urban: 'Scientific name (optional). Example: "Momordica charantia" untuk Peria Katak. Use untuk scientific credibility.',
    en: 'Scientific name (optional). Example: "Momordica charantia" for bitter gourd. Used for scientific credibility.'
  },
  
  // Section 3: Benefits
  benefit: {
    bm: 'Faedah produk untuk pelanggan. Fokus pada hasil/outcome. Contoh: "Bantu imbangkan insulin", "Kurangkan sugar cravings"',
    urban: 'Product benefit untuk customer. Focus on results/outcome. Example: "Helps balance insulin", "Reduces sugar cravings"',
    en: 'Product benefit for customers. Focus on results/outcomes. Example: "Helps balance insulin", "Reduces sugar cravings"'
  },
  
  // Section 4: Social Proof
  testimonial: {
    bm: 'Testimoni pelanggan sebenar. Sertakan nama dan hasil yang dialami. Contoh: "Gula darah saya turun dari 12 ke 6 dalam 3 bulan! - Encik Ahmad"',
    urban: 'Real customer testimonial. Include name dan results experienced. Example: "My blood sugar dropped from 12 to 6 dalam 3 months! - En. Ahmad"',
    en: 'Real customer testimonial. Include name and results experienced. Example: "My blood sugar dropped from 12 to 6 in 3 months! - Mr. Ahmad"'
  },
  
  // Section 5: Commercial
  pricePoint: {
    bm: 'Harga dan pakej. Contoh: "RM 89.90 per kotak (10 sachets)", "RM 249 untuk 3-kotak bundle (jimat 15%)"',
    urban: 'Price dan packages. Example: "RM 89.90 per box (10 sachets)", "RM 249 for 3-box bundle (save 15%)"',
    en: 'Pricing and packages. Example: "RM 89.90 per box (10 sachets)", "RM 249 for 3-box bundle (save 15%)"'
  },
  
  // Section 6: Research
  studyTitle: {
    bm: 'Tajuk kajian penyelidikan. Contoh: "Kesan Peria Katak terhadap Kawalan Glikemik pada Pesakit Diabetes Jenis 2"',
    urban: 'Research study title. Example: "Effects of Bitter Gourd on Glycemic Control dalam Type 2 Diabetes Patients"',
    en: 'Research study title. Example: "Effects of Bitter Gourd on Glycemic Control in Type 2 Diabetes Patients"'
  },
  
  // Section 7: System
  versionNotes: {
    bm: 'Nota versi - apa yang berubah? Contoh: "Update harga promosi, tambah 2 testimoni baru"',
    urban: 'Version notes - what changed? Example: "Updated promo pricing, added 2 new testimonials"',
    en: 'Version notes - what changed? Example: "Updated promotional pricing, added 2 new testimonials"'
  }
};

// Field labels translations
const FIELD_LABELS = {
  productName: {
    bm: 'Nama Produk',
    urban: 'Nama Product',
    en: 'Product Name'
  },
  description: {
    bm: 'Penerangan Lengkap',
    urban: 'Full Description',
    en: 'Full Description'
  },
  usp: {
    bm: 'Kelebihan Unik (USP)',
    urban: 'Unique Selling Points (USP)',
    en: 'Unique Selling Points (USP)'
  },
  targetCustomer: {
    bm: 'Sasaran Pelanggan',
    urban: 'Target Customer',
    en: 'Target Customer'
  },
  shortDesc: {
    bm: 'Penerangan Ringkas',
    urban: 'Short Description',
    en: 'Short Description'
  },
  ingredientName: {
    bm: 'Nama Ramuan',
    urban: 'Nama Ingredient',
    en: 'Ingredient Name'
  },
  scientificName: {
    bm: 'Nama Saintifik',
    urban: 'Scientific Name',
    en: 'Scientific Name'
  },
  benefit: {
    bm: 'Faedah Produk',
    urban: 'Product Benefit',
    en: 'Product Benefit'
  },
  testimonial: {
    bm: 'Testimoni Pelanggan',
    urban: 'Customer Testimonial',
    en: 'Customer Testimonial'
  },
  pricePoint: {
    bm: 'Harga & Pakej',
    urban: 'Price & Packages',
    en: 'Price & Packages'
  },
  studyTitle: {
    bm: 'Tajuk Kajian',
    urban: 'Study Title',
    en: 'Study Title'
  },
  versionNotes: {
    bm: 'Nota Versi',
    urban: 'Version Notes',
    en: 'Version Notes'
  },
  placeholder: {
    bm: 'Taip dalam BM, EN, atau campur...',
    urban: 'Type dalam BM, EN, atau mix...',
    en: 'Type in BM, EN, or mix...'
  }
};

// Debounce hook for iOS keyboard stability
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Debounced Input Component - prevents keyboard dismissal on iOS
const DebouncedInput = ({ 
  initialValue,
  onChangeComplete, 
  placeholder, 
  className,
  type = 'text',
  ...props 
}) => {
  const [localValue, setLocalValue] = useState(initialValue || '');
  const debouncedValue = useDebounce(localValue, 500);
  
  // Only notify parent when debounced value changes (same pattern as IngredientItem)
  useEffect(() => {
    if (debouncedValue !== (initialValue || '') && onChangeComplete) {
      onChangeComplete(debouncedValue);
    }
  }, [debouncedValue]);
  
  return (
    <input
      type={type}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  );
};

// Debounced Textarea Component - prevents keyboard dismissal on iOS
const DebouncedTextarea = ({ 
  initialValue,
  onChangeComplete, 
  placeholder, 
  className,
  rows = 4,
  ...props 
}) => {
  const [localValue, setLocalValue] = useState(initialValue || '');
  const debouncedValue = useDebounce(localValue, 500);
  
  // Only notify parent when debounced value changes (same pattern as IngredientItem)
  useEffect(() => {
    if (debouncedValue !== (initialValue || '') && onChangeComplete) {
      onChangeComplete(debouncedValue);
    }
  }, [debouncedValue]);
  
  return (
    <textarea
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      placeholder={placeholder}
      className={className}
      rows={rows}
      {...props}
    />
  );
};

// Testimonial Item Component with debouncing
const TestimonialItem = ({ testimonial, onUpdate, onRemove, index }) => {
  const [localUserName, setLocalUserName] = useState(testimonial.user_name || '');
  const [localProfile, setLocalProfile] = useState(testimonial.profile || '');
  const [localFeedback, setLocalFeedback] = useState(testimonial.feedback_text || '');
  const [localDuration, setLocalDuration] = useState(testimonial.duration_used || '');
  
  const debouncedUserName = useDebounce(localUserName, 500);
  const debouncedProfile = useDebounce(localProfile, 500);
  const debouncedFeedback = useDebounce(localFeedback, 500);
  const debouncedDuration = useDebounce(localDuration, 500);
  
  useEffect(() => {
    if (debouncedUserName !== testimonial.user_name) {
      onUpdate({ ...testimonial, user_name: debouncedUserName });
    }
  }, [debouncedUserName]);
  
  useEffect(() => {
    if (debouncedProfile !== testimonial.profile) {
      onUpdate({ ...testimonial, profile: debouncedProfile });
    }
  }, [debouncedProfile]);
  
  useEffect(() => {
    if (debouncedFeedback !== testimonial.feedback_text) {
      onUpdate({ ...testimonial, feedback_text: debouncedFeedback });
    }
  }, [debouncedFeedback]);
  
  useEffect(() => {
    if (debouncedDuration !== testimonial.duration_used) {
      onUpdate({ ...testimonial, duration_used: debouncedDuration });
    }
  }, [debouncedDuration]);
  
  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          value={localUserName}
          onChange={(e) => setLocalUserName(e.target.value)}
          placeholder="Customer Name"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={localProfile}
          onChange={(e) => setLocalProfile(e.target.value)}
          placeholder="Profile (e.g., Age 35, Teacher)"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <textarea
        value={localFeedback}
        onChange={(e) => setLocalFeedback(e.target.value)}
        placeholder="Testimonial text"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-2"
        rows={3}
      />
      <div className="flex items-center gap-3 mb-2">
        <select
          value={testimonial.rating || 5}
          onChange={(e) => onUpdate({ ...testimonial, rating: parseInt(e.target.value) })}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value={5}>⭐⭐⭐⭐⭐ (5 stars)</option>
          <option value={4}>⭐⭐⭐⭐ (4 stars)</option>
          <option value={3}>⭐⭐⭐ (3 stars)</option>
        </select>
        <input
          type="text"
          value={localDuration}
          onChange={(e) => setLocalDuration(e.target.value)}
          placeholder="Duration (e.g., 3 weeks)"
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="text-sm text-red-600 hover:underline"
      >
        Remove Testimonial
      </button>
    </div>
  );
};

// Ambassador Item Component with debouncing and Knowledge Pack linking display
const AmbassadorItem = ({ ambassador, onUpdate, onRemove, index }) => {
  const [localName, setLocalName] = useState(ambassador.name || '');
  const [localSocialHandle, setLocalSocialHandle] = useState(ambassador.social_handle || '');
  const [localEndorsement, setLocalEndorsement] = useState(ambassador.endorsement_text || '');
  const [showPersona, setShowPersona] = useState(false);
  
  const debouncedName = useDebounce(localName, 500);
  const debouncedSocialHandle = useDebounce(localSocialHandle, 500);
  const debouncedEndorsement = useDebounce(localEndorsement, 500);
  
  useEffect(() => {
    if (debouncedName !== ambassador.name) {
      onUpdate({ ...ambassador, name: debouncedName });
    }
  }, [debouncedName]);
  
  useEffect(() => {
    if (debouncedSocialHandle !== ambassador.social_handle) {
      onUpdate({ ...ambassador, social_handle: debouncedSocialHandle });
    }
  }, [debouncedSocialHandle]);
  
  useEffect(() => {
    if (debouncedEndorsement !== ambassador.endorsement_text) {
      onUpdate({ ...ambassador, endorsement_text: debouncedEndorsement });
    }
  }, [debouncedEndorsement]);
  
  // Check if linked to Knowledge Pack
  const isLinked = !!ambassador.ambassador_pack_id;
  const personaSnapshot = ambassador.persona_snapshot || {};
  
  return (
    <div className={`p-4 border-2 rounded-lg ${isLinked ? 'border-purple-400 bg-purple-50' : 'border-purple-200 bg-purple-50'}`}>
      {/* Linked Badge */}
      {isLinked && (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-purple-200">
          <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold rounded-full">
            🎭 Linked to Ambassador Pack
          </span>
          {personaSnapshot.persona_brand && (
            <span className="text-xs text-purple-700 font-semibold">
              {personaSnapshot.persona_brand}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowPersona(!showPersona)}
            className="ml-auto text-xs text-purple-600 hover:underline"
          >
            {showPersona ? 'Hide Persona' : 'Show Persona'} {showPersona ? '▼' : '▶'}
          </button>
        </div>
      )}
      
      {/* Persona Preview (if linked and expanded) */}
      {isLinked && showPersona && personaSnapshot && (
        <div className="mb-3 p-3 bg-white rounded-lg border border-purple-200">
          <div className="text-xs space-y-2">
            <div><strong className="text-purple-700">Full Name:</strong> {personaSnapshot.full_name || 'N/A'}</div>
            {personaSnapshot.tone_profile && (
              <div><strong className="text-purple-700">Tone:</strong> {personaSnapshot.tone_profile.primary || 'N/A'} - {personaSnapshot.tone_profile.secondary || ''}</div>
            )}
            {personaSnapshot.emotional_signature && (
              <div><strong className="text-purple-700">Emotion:</strong> {personaSnapshot.emotional_signature.primary_emotion || 'N/A'}</div>
            )}
            {personaSnapshot.story_themes && personaSnapshot.story_themes.length > 0 && (
              <div><strong className="text-purple-700">Story Themes:</strong> {personaSnapshot.story_themes.join(', ')}</div>
            )}
            {personaSnapshot.language_preference && personaSnapshot.language_preference.length > 0 && (
              <div><strong className="text-purple-700">Languages:</strong> {personaSnapshot.language_preference.join(', ')}</div>
            )}
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
        <input
          type="text"
          value={localName}
          onChange={(e) => setLocalName(e.target.value)}
          placeholder="Ambassador Name"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          disabled={isLinked}
        />
        <select
          value={ambassador.type || 'Influencer'}
          onChange={(e) => onUpdate({ ...ambassador, type: e.target.value })}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        >
          <option value="Influencer">Influencer</option>
          <option value="Expert">Expert</option>
          <option value="Doctor">Doctor</option>
          <option value="Nutritionist">Nutritionist</option>
          <option value="Celebrity">Celebrity</option>
          <option value="Customer">Customer</option>
        </select>
      </div>
      <input
        type="text"
        value={localSocialHandle}
        onChange={(e) => setLocalSocialHandle(e.target.value)}
        placeholder="Social Handle (e.g., @influencer)"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 mb-2"
      />
      <textarea
        value={localEndorsement}
        onChange={(e) => setLocalEndorsement(e.target.value)}
        placeholder="Endorsement text"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        rows={2}
      />
      <button
        type="button"
        onClick={onRemove}
        className="text-sm text-red-600 hover:underline mt-2"
      >
        {isLinked ? 'Unlink & Remove Ambassador' : 'Remove Ambassador'}
      </button>
    </div>
  );
};

// Identity Section Component - Using EXACT pattern as IngredientItem (which works on iOS)
const IdentitySection = ({ identity, onUpdate, onMarkChanged, hasChanges, lastSaved, saving, onSave, onCancel, t, help, language }) => {
  // Local state for inputs - EXACT same pattern as IngredientItem
  const [localPackName, setLocalPackName] = useState(
    identity?.product_name?.en || identity?.product_name?.ms || 
    identity?.pack_name?.en || identity?.pack_name?.ms || ''
  );
  const [localDescription, setLocalDescription] = useState(identity?.description?.en || identity?.description?.ms || '');
  
  // Update local state when identity prop changes (when pack data loads)
  useEffect(() => {
    const loadedPackName = identity?.product_name?.en || identity?.product_name?.ms || 
                           identity?.pack_name?.en || identity?.pack_name?.ms || '';
    if (loadedPackName && loadedPackName !== localPackName) {
      console.log('📝 [IDENTITY] Updating localPackName from loaded data:', loadedPackName);
      setLocalPackName(loadedPackName);
    }
  }, [identity?.pack_name, identity?.product_name]);
  
  useEffect(() => {
    const loadedDescription = identity?.description?.en || identity?.description?.ms || '';
    if (loadedDescription && loadedDescription !== localDescription) {
      console.log('📝 [IDENTITY] Updating localDescription from loaded data:', loadedDescription.substring(0, 50));
      setLocalDescription(loadedDescription);
    }
  }, [identity?.description]);
  
  // Debounce values - EXACT same pattern as IngredientItem
  const debouncedPackName = useDebounce(localPackName, 500);
  const debouncedDescription = useDebounce(localDescription, 500);
  
  // Update parent only when debounced value changes - EXACT same pattern as IngredientItem
  useEffect(() => {
    if (debouncedPackName && debouncedPackName !== (identity?.pack_name?.en || identity?.pack_name?.ms || identity?.product_name?.en || identity?.product_name?.ms || '')) {
      console.log('📤 [IDENTITY] Sending product_name update to parent:', debouncedPackName);
      onUpdate({
        ...identity,
        pack_name: { en: debouncedPackName, ms: debouncedPackName },
        product_name: { en: debouncedPackName, ms: debouncedPackName }  // Save to both for compatibility
      });
      onMarkChanged();
    }
  }, [debouncedPackName]);
  
  useEffect(() => {
    if (debouncedDescription && debouncedDescription !== (identity?.description?.en || identity?.description?.ms || '')) {
      console.log('📤 [IDENTITY] Sending description update to parent:', debouncedDescription.substring(0, 50));
      onUpdate({
        ...identity,
        description: { en: debouncedDescription, ms: debouncedDescription }
      });
      onMarkChanged();
    }
  }, [debouncedDescription]);
  
  return (
    <div className="p-4 border-t border-gray-200 relative">
      <div className="space-y-4 mb-4">
        {/* Product Name */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('productName')} *
          </label>
          
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            marginBottom: '8px',
            padding: '8px',
            background: '#f9fafb',
            borderRadius: '6px',
            borderLeft: '3px solid #3b82f6',
            lineHeight: '1.6'
          }}>
            💡 {help('productName')}
          </div>
          
          <input
            type="text"
            value={localPackName}
            onChange={(e) => setLocalPackName(e.target.value)}
            placeholder={t('placeholder')}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        {/* Description */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('description')}
          </label>
          
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            marginBottom: '8px',
            padding: '8px',
            background: '#f9fafb',
            borderRadius: '6px',
            borderLeft: '3px solid #3b82f6',
            lineHeight: '1.6'
          }}>
            💡 {help('description')}
          </div>
          
          <textarea
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder={t('placeholder')}
            rows={4}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-vertical"
          />
        </div>
        
        {/* USPs */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            Unique Selling Points
          </label>
          <div className="space-y-2">
            {identity.unique_selling_points?.map((usp, i) => (
              <USPItem
                key={usp.id || i}
                usp={usp}
                onUpdate={(updated) => {
                  const newUSPs = [...(identity.unique_selling_points || [])];
                  newUSPs[i] = updated;
                  onUpdate({ ...identity, unique_selling_points: newUSPs });
                  onMarkChanged();
                }}
                onRemove={() => {
                  const newUSPs = identity.unique_selling_points.filter((_, idx) => idx !== i);
                  onUpdate({ ...identity, unique_selling_points: newUSPs });
                  onMarkChanged();
                }}
              />
            ))}
            <button
              type="button"
              onClick={() => {
                const newUSP = {
                  id: `usp_${Date.now()}`,
                  title: '',
                  proof: '',
                  customer_perception: '',
                  marketing_angle: '',
                  priority: (identity.unique_selling_points?.length || 0) + 1
                };
                onUpdate({
                  ...identity,
                  unique_selling_points: [...(identity.unique_selling_points || []), newUSP]
                });
                onMarkChanged();
              }}
              className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg border border-dashed border-blue-300"
            >
              + Add USP
            </button>
          </div>
        </div>
      </div>
      
      <SectionSaveBar
        section="identity"
        hasChanges={hasChanges}
        lastSaved={lastSaved}
        saving={saving}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
};

// Section Save Bar Component
const SectionSaveBar = ({ section, hasChanges, lastSaved, saving, onSave, onCancel }) => {
  if (!hasChanges && !lastSaved) return null;
  
  return (
    <div className="sticky bottom-0 left-0 right-0 bg-white border-t-2 border-blue-600 p-3 shadow-lg rounded-t-xl z-20">
      {/* Status message - repositioned above buttons */}
      <div className="text-xs mb-2">
        {hasChanges && !saving && (
          <span className="text-orange-600 font-semibold">● Unsaved changes</span>
        )}
        {lastSaved && !hasChanges && (
          <span className="text-green-600 text-xs">✅ Last saved - {lastSaved}</span>
        )}
      </div>
      
      {/* Buttons below status */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all"
        >
          {saving ? '💾 Saving...' : '💾 Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// USP Item Component
const USPItem = ({ usp, onUpdate, onRemove }) => {
  const [localTitle, setLocalTitle] = useState(usp.title || '');
  const [expanded, setExpanded] = useState(false);
  const debouncedTitle = useDebounce(localTitle, 500);
  
  useEffect(() => {
    if (debouncedTitle !== usp.title) {
      onUpdate({ ...usp, title: debouncedTitle });
    }
  }, [debouncedTitle]);
  
  return (
    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-start gap-3 mb-2">
        <input
          type="text"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          placeholder="USP Title (e.g., Halal herbal coffee)"
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          {expanded ? '▼' : '▶'}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
        >
          ✕
        </button>
      </div>
      {expanded && (
        <div className="space-y-2 mt-2">
          <textarea
            value={usp.proof || ''}
            onChange={(e) => onUpdate({ ...usp, proof: e.target.value })}
            placeholder="Proof (scientific backing)"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows={2}
          />
          <textarea
            value={usp.customer_perception || ''}
            onChange={(e) => onUpdate({ ...usp, customer_perception: e.target.value })}
            placeholder="Customer Perception"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows={2}
          />
          <textarea
            value={usp.marketing_angle || ''}
            onChange={(e) => onUpdate({ ...usp, marketing_angle: e.target.value })}
            placeholder="Marketing Angle"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows={2}
          />
        </div>
      )}
    </div>
  );
};

// Active Compound Item Component
const ActiveCompoundItem = ({ compound, onUpdate, onRemove }) => {
  const [localName, setLocalName] = useState(compound.name || '');
  const debouncedName = useDebounce(localName, 500);
  
  useEffect(() => {
    if (debouncedName !== compound.name) {
      onUpdate({ ...compound, name: debouncedName });
    }
  }, [debouncedName]);
  
  return (
    <div className="p-2 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-2">
      <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
        {compound.category}
      </span>
      <input
        type="text"
        value={localName}
        onChange={(e) => setLocalName(e.target.value)}
        placeholder="Compound name"
        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
      />
      <button
        type="button"
        onClick={onRemove}
        className="text-red-600 hover:text-red-800 text-sm"
      >
        ✕
      </button>
    </div>
  );
};

// Evidence Card Component
const EvidenceCard = ({ evidence, compoundName }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left transition-colors"
      >
        <div className="flex-1">
          <div className="font-semibold text-sm text-gray-800">
            {compoundName || 'General Evidence'}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            {evidence.mode} • Confidence: {(evidence.confidence * 100).toFixed(0)}%
          </div>
        </div>
        <div className="text-gray-500">{isOpen ? '▼' : '▶'}</div>
      </button>
      
      {isOpen && (
        <div className="p-4 bg-white">
          <div className="text-sm text-gray-700 mb-3">
            {evidence.summary}
          </div>
          
          {evidence.references && evidence.references.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-semibold text-gray-600 mb-2">References:</div>
              <div className="space-y-1">
                {evidence.references.map((ref, i) => (
                  <div key={`item-${i}`} className="text-xs text-gray-600">
                    <span className="font-medium">{ref.title}</span>
                    {ref.source && <span className="text-gray-500"> • {ref.source}</span>}
                    {ref.url && (
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline ml-2"
                      >
                        🔗
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {evidence.provenance && (
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                {evidence.provenance.model}
              </span>
              <span>{evidence.provenance.latency_ms}ms</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Ingredient Item Component
// SIMPLIFIED IngredientItem for Section 2
// Only for basic ingredient input - no AI generation, no compounds, no evidence
const IngredientItemSimple = ({ 
  ingredient, 
  onUpdate, 
  onRemove
}) => {
  const [localCommonName, setLocalCommonName] = useState(ingredient.common_name?.en || '');
  const [localScientificName, setLocalScientificName] = useState(ingredient.scientific_name || '');
  const [localNarrative, setLocalNarrative] = useState(ingredient.narrative || '');
  
  const debouncedCommonName = useDebounce(localCommonName, 500);
  const debouncedScientificName = useDebounce(localScientificName, 500);
  const debouncedNarrative = useDebounce(localNarrative, 500);
  
  useEffect(() => {
    if (debouncedCommonName !== ingredient.common_name?.en) {
      onUpdate({
        ...ingredient,
        common_name: { ...ingredient.common_name, en: debouncedCommonName }
      });
    }
  }, [debouncedCommonName]);
  
  useEffect(() => {
    if (debouncedScientificName !== ingredient.scientific_name) {
      onUpdate({
        ...ingredient,
        scientific_name: debouncedScientificName
      });
    }
  }, [debouncedScientificName]);
  
  useEffect(() => {
    if (debouncedNarrative !== ingredient.narrative) {
      onUpdate({
        ...ingredient,
        narrative: debouncedNarrative
      });
    }
  }, [debouncedNarrative]);
  
  return (
    <div className="p-4 border border-gray-300 rounded-xl bg-white">
      {/* Ingredient Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          value={localCommonName}
          onChange={(e) => setLocalCommonName(e.target.value)}
          placeholder="Common Name (required) *"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={localScientificName}
          onChange={(e) => setLocalScientificName(e.target.value)}
          placeholder="Scientific Name (optional)"
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>
      
      {/* Narrative/Focus Area (optional) */}
      <div className="mb-3">
        <input
          type="text"
          value={localNarrative}
          onChange={(e) => setLocalNarrative(e.target.value)}
          placeholder="Narrative/Focus Area (optional) - e.g., 'blood sugar control', 'anti-inflammatory', 'digestive health'"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-blue-50"
        />
        <p className="text-xs text-gray-500 mt-1">
          💡 Guide AI to focus on specific health benefits - use this in Section 3 when generating evidence
        </p>
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">Go to Section 3 to generate evidence & benefits</span>
        
        {/* Remove Ingredient Button */}
        <button
          type="button"
          onClick={onRemove}
          className="text-sm text-red-600 hover:underline"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

// COMPLETE IngredientEvidenceCard for Section 3
// Shows ingredient with Generate AI + compounds + evidence + benefits together
const IngredientEvidenceCard = ({ 
  ingredient, 
  onUpdate,
  onGenerateEvidence, 
  generating,
  benefits,
  onAcceptBenefit,
  onAcceptAllBenefits,
  onDeleteBenefit,
  onEditBenefit
}) => {
  const [showCompounds, setShowCompounds] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [resolvedMode, setResolvedMode] = useState(null);
  
  const ingredientName = ingredient.common_name?.en || ingredient.common_name?.ms || 'Unnamed Ingredient';
  const ingredientId = ingredient.id;
  
  // Filter benefits for this ingredient
  const aiSuggestedBenefits = benefits.filter(b => 
    b.ai_suggested && 
    !b.accepted && 
    (b.source_ingredient_name === ingredientName || b.source_ingredient_id === ingredientId)
  );
  
  const acceptedBenefits = benefits.filter(b => 
    b.accepted && 
    (b.source_ingredient_name === ingredientName || b.source_ingredient_id === ingredientId)
  );
  
  const handleGenerate = async () => {
    console.log('🎯 [Generate Evidence for Ingredient]', {
      ingredientId: ingredient.id,
      commonName: ingredientName,
      timestamp: new Date().toISOString()
    });
    
    try {
      await onGenerateEvidence(ingredient.id, null, (mode) => {
        setResolvedMode(mode);
      });
    } catch (error) {
      // Error is already handled in onGenerateEvidence
    }
  };
  
  const hasEvidence = ingredient.active_compounds?.length > 0 || ingredient.ai_generated_evidence?.length > 0;
  
  return (
    <div className="border-2 border-gray-200 rounded-xl bg-gray-50 overflow-hidden">
      {/* Ingredient Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-bold text-gray-800">{ingredientName}</h4>
            {ingredient.scientific_name && (
              <p className="text-sm text-gray-600 italic">{ingredient.scientific_name}</p>
            )}
            {ingredient.narrative && (
              <p className="text-xs text-gray-500 mt-1">
                🎯 Focus: {ingredient.narrative}
              </p>
            )}
          </div>
          
          {/* Generate Evidence Button */}
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating === ingredient.id}
            className="px-4 py-2 text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
          >
            {generating === ingredient.id ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Analyzing...
              </span>
            ) : hasEvidence ? '🔄 Regenerate' : '🔬 Generate Evidence'}
          </button>
        </div>
        
        {/* Display resolved mode after generation */}
        {resolvedMode && (
          <div className="mt-2 text-xs text-gray-600 italic bg-white px-3 py-1 rounded-lg inline-block">
            AI Mode: <span className="font-semibold text-purple-600">{resolvedMode}</span>
          </div>
        )}
      </div>
      
      {/* Evidence Content */}
      <div className="p-4 space-y-4">
        {/* Active Compounds */}
        {ingredient.active_compounds && ingredient.active_compounds.length > 0 && (
          <div className="bg-white rounded-lg border border-blue-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <span className="text-blue-600">⚗️</span>
                Active Compounds ({ingredient.active_compounds.length})
              </h5>
              <button
                type="button"
                onClick={() => setShowCompounds(!showCompounds)}
                className="text-xs text-blue-600 hover:underline font-semibold"
              >
                {showCompounds ? 'Hide' : 'Show'}
              </button>
            </div>
            {showCompounds && (
              <div className="space-y-2 mt-3">
                {ingredient.active_compounds.map((compound, i) => (
                  <div key={compound.id || i} className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm font-semibold text-gray-800">{txt(compound.name)}</p>
                    {compound.description && (
                      <p className="text-xs text-gray-600 mt-1">{txt(compound.description)}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Evidence Entries */}
        {ingredient.ai_generated_evidence && ingredient.ai_generated_evidence.length > 0 && (
          <div className="bg-white rounded-lg border border-green-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <span className="text-green-600">📚</span>
                Evidence Entries ({ingredient.ai_generated_evidence.length})
              </h5>
              <button
                type="button"
                onClick={() => setShowEvidence(!showEvidence)}
                className="text-xs text-green-600 hover:underline font-semibold"
              >
                {showEvidence ? 'Hide' : 'Show'}
              </button>
            </div>
            {showEvidence && (
              <div className="space-y-3 mt-3">
                {ingredient.ai_generated_evidence.map((ev, i) => (
                  <div key={ev.id || i} className="p-3 bg-green-50 rounded-lg border border-green-100">
                    <p className="text-xs font-semibold text-gray-700 mb-2">{ev.title || 'Evidence Entry'}</p>
                    <p className="text-xs text-gray-600 mb-2">{ev.description || ev.summary}</p>
                    
                    {/* Confidence Score */}
                    {ev.confidence && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-gray-500">Confidence:</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[100px]">
                          <div 
                            className="bg-green-600 h-1.5 rounded-full" 
                            style={{ width: `${ev.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-green-700">{(ev.confidence * 100).toFixed(0)}%</span>
                      </div>
                    )}
                    
                    {/* References with Links */}
                    {ev.references && ev.references.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <p className="text-xs font-semibold text-gray-700 mb-1">📖 References & Further Study:</p>
                        <div className="space-y-1">
                          {ev.references.map((ref, refIndex) => (
                            <div key={refIndex} className="text-xs">
                              <p className="text-gray-700 font-medium">{ref.title}</p>
                              <p className="text-gray-500 text-xs">
                                {ref.source} {ref.year && `(${ref.year})`}
                              </p>
                              <div className="flex gap-2 mt-0.5">
                                {ref.url && (
                                  <a 
                                    href={ref.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline text-xs flex items-center gap-1"
                                  >
                                    🔗 Read Study
                                  </a>
                                )}
                                {ref.doi && (
                                  <a 
                                    href={`https://doi.org/${ref.doi}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-purple-600 hover:underline text-xs flex items-center gap-1"
                                  >
                                    📄 DOI: {ref.doi}
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Fallback for old format */}
                    {(!ev.references || ev.references.length === 0) && ev.source && (
                      <p className="text-xs text-gray-500 mt-2 italic">Source: {ev.source}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* AI Suggested Benefits */}
        {aiSuggestedBenefits.length > 0 && (
          <div className="bg-white rounded-lg border border-purple-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <span className="text-purple-600">✨</span>
                AI Suggested Benefits ({aiSuggestedBenefits.length})
              </h5>
              <button
                type="button"
                onClick={() => onAcceptAllBenefits(ingredientId)}
                className="px-3 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                ✓ Accept All
              </button>
            </div>
            <div className="space-y-2 mt-3">
              {aiSuggestedBenefits.map((benefit) => (
                <div key={benefit.id} className="p-3 bg-purple-50 rounded-lg border border-purple-100 flex items-start justify-between">
                  <p className="text-sm text-gray-800 flex-1">{benefit.title}</p>
                  <button
                    type="button"
                    onClick={() => onAcceptBenefit(benefit.id)}
                    className="ml-3 px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    ✓ Accept
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Accepted Benefits */}
        {acceptedBenefits.length > 0 && (
          <div className="bg-white rounded-lg border border-green-200 p-3">
            <h5 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-3">
              <span className="text-green-600">✅</span>
              Accepted Benefits ({acceptedBenefits.length})
            </h5>
            <div className="space-y-2">
              {acceptedBenefits.map((benefit) => (
                <div key={benefit.id} className="p-3 bg-green-50 rounded-lg border border-green-100 flex items-start justify-between">
                  <p className="text-sm text-gray-800 flex-1">{benefit.title}</p>
                  <button
                    type="button"
                    onClick={() => onDeleteBenefit(benefit.id)}
                    className="ml-3 text-xs text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {!hasEvidence && aiSuggestedBenefits.length === 0 && acceptedBenefits.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">🔬 Click "Generate Evidence" to analyze this ingredient with AI</p>
            <p className="text-xs mt-2">AI will generate compounds, evidence, and benefits</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Benefit Chip Component with Provenance
const BenefitChip = ({ benefit, onAccept, onEdit, onDelete, onHover }) => {
  const isAI = benefit.ai_suggested && !benefit.accepted;
  const isAccepted = benefit.accepted || !benefit.ai_suggested;
  
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all ${
        isAI
          ? 'bg-blue-50 border border-blue-300'
          : 'bg-green-100 border border-green-300'
      }`}
      onMouseEnter={() => onHover && onHover(benefit)}
      onMouseLeave={() => onHover && onHover(null)}
    >
      {isAI && (
        <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">AI</span>
      )}
      {isAccepted && benefit.ai_suggested && (
        <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">✓</span>
      )}
      <span className="text-gray-800">{benefit.title}</span>
      {isAI && (
        <>
          <button
            type="button"
            onClick={() => onAccept(benefit.id)}
            className="text-green-600 hover:text-green-800 text-xs font-bold"
            title="Accept"
          >
            ✓
          </button>
          <button
            type="button"
            onClick={() => onEdit(benefit.id)}
            className="text-blue-600 hover:text-blue-800 text-xs"
            title="Edit"
          >
            ✎
          </button>
        </>
      )}
      <button
        type="button"
        onClick={() => onDelete(benefit.id)}
        className="text-red-600 hover:text-red-800 text-xs"
        title="Delete"
      >
        ✕
      </button>
    </div>
  );
};

// Research Study Card Component
const ResearchStudyCard = ({ study, onLink, onRemove }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="p-4 border border-purple-200 rounded-lg bg-purple-50">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-semibold text-sm text-gray-800">{study.title}</h4>
          <div className="text-xs text-gray-600 mt-1">
            {study.authors?.join(', ')} • {study.institution} • {study.year}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              study.evidence_level === 'High' ? 'bg-green-100 text-green-700' :
              study.evidence_level === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {study.evidence_level}
            </span>
            <span className="text-xs text-gray-500">
              Confidence: {(study.confidence * 100).toFixed(0)}%
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="ml-2 text-gray-500 hover:text-gray-700"
        >
          {expanded ? '▼' : '▶'}
        </button>
      </div>
      
      {expanded && (
        <div className="mt-3 space-y-2">
          {study.abstract && (
            <div className="text-xs text-gray-700">
              <span className="font-semibold">Abstract:</span> {study.abstract}
            </div>
          )}
          {study.key_findings && study.key_findings.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-gray-700">Key Findings:</span>
              <ul className="list-disc list-inside text-xs text-gray-600 mt-1">
                {study.key_findings.map((finding, i) => (
                  <li key={`item-${i}`}>{finding}</li>
                ))}
              </ul>
            </div>
          )}
          {study.doi && (
            <div className="text-xs text-gray-600">
              <span className="font-semibold">DOI:</span>{' '}
              <a href={`https://doi.org/${study.doi}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {study.doi}
              </a>
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onLink(study.id)}
              className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Link to Benefits
            </button>
            <button
              type="button"
              onClick={() => onRemove(study.id)}
              className="px-3 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ProductPackEditorV4({ 
  packData,
  onUpdate,
  packId: propPackId, 
  userId: propUserId 
}) {
  // Extract packId from URL params or use prop
  const params = useParams();
  const packId = params.packId || propPackId;
  
  // Get current user from auth context
  const { user } = useAuth();
  const userId = propUserId || user?.email || user?.user_id || 'anonymous';
  
  // Language context for multilingual support
  const { language } = useLanguage();
  
  // Helper function to get translated text
  const t = (key) => FIELD_LABELS[key]?.[language] || FIELD_LABELS[key]?.en || key;
  const help = (key) => FIELD_HELPERS[key]?.[language] || FIELD_HELPERS[key]?.en || '';
  
  // Section states
  const [openSections, setOpenSections] = useState({
    identity: true,
    ingredients: false,
    benefits: false,
    social: false,
    commercial: false,
    research: false,
    system: false
  });
  
  // Pack version detection
  const [packVersion, setPackVersion] = useState(null); // 'v3' or 'v4'
  const [isMigrating, setIsMigrating] = useState(false);
  const [showMigrationBanner, setShowMigrationBanner] = useState(false);
  
  // Detect pack version on mount
  useEffect(() => {
    const detectVersion = async () => {
      try {
                console.log('🔍 Detecting pack version for:', packId);
        
        // Try to fetch from V4 collection first
        const v4Response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}`, {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        });
        
        if (v4Response.ok) {
          console.log('✅ Pack is V4');
          setPackVersion('v4');
          setShowMigrationBanner(false);
        } else if (v4Response.status === 404) {
          console.log('⚠️ Pack not found in V4, likely V3');
          setPackVersion('v3');
          setShowMigrationBanner(true);
        } else {
          console.error('❌ Error detecting version:', v4Response.status);
        }
      } catch (error) {
        console.error('❌ Version detection error:', error);
        // Assume V3 if detection fails
        setPackVersion('v3');
      }
    };
    
    if (packId) {
      detectVersion();
    }
  }, [packId]);
  
  // Migrate V3 to V4
  const migrateToV4 = useCallback(async () => {
    setIsMigrating(true);
    const toastId = toast.loading('🔄 Migrating pack to Product Pack V4...');
    
    try {
            console.log('🔄 Migrating V3 pack to V4:', packId);
      
      const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/migrate/v3-to-v4/${packId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Migration failed');
      }
      
      const data = await response.json();
      console.log('✅ Migration successful:', data);
      
      // Update pack version state
      setPackVersion('v4');
      setShowMigrationBanner(false);
      
      toast.success('✅ Pack migrated to V4 successfully!', { id: toastId, duration: 4000 });
      
      // Trigger reload if needed
      if (onUpdate) onUpdate();
      
      return data.v4_pack_id;
    } catch (error) {
      console.error('❌ Migration error:', error);
      toast.error(`❌ Migration failed: ${error.message}`, { id: toastId, duration: 6000 });
      throw error;
    } finally {
      setIsMigrating(false);
    }
  }, [packId, onUpdate]);
  
  // Pack data state (for when used as route component)
  const [loadedPackData, setLoadedPackData] = useState(packData || null);
  const [isLoading, setIsLoading] = useState(!packData);
  
  // Fetch pack data if not provided via props (route usage)
  useEffect(() => {
    const fetchPackData = async () => {
      if (packData || !packId) return; // Skip if data already provided or no packId
      
      setIsLoading(true);
      try {
                console.log('📥 Fetching pack data for:', packId);
        
        const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}`, {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            console.warn('⚠️ Pack not found in V4, may need migration');
            setPackVersion('v3');
            setShowMigrationBanner(true);
          } else {
            throw new Error(`Failed to fetch pack: ${response.status}`);
          }
          return;
        }
        
        const data = await response.json();
        console.log('✅ Pack data loaded:', data);
        setLoadedPackData(data);
        setPackVersion('v4');
      } catch (error) {
        console.error('❌ Error fetching pack data:', error);
        toast.error('Failed to load pack data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPackData();
  }, [packId, packData]);
  
  // Use loaded data or prop data
  const activePackData = loadedPackData || packData;
  
  // Draft states per section
  const [draftIdentity, setDraftIdentity] = useState({});
  const [draftIngredients, setDraftIngredients] = useState([]);
  const [draftBenefits, setDraftBenefits] = useState([]);
  const [draftSocial, setDraftSocial] = useState({});
  const [draftCommercial, setDraftCommercial] = useState({});
  const [draftResearch, setDraftResearch] = useState({});
  
  // Update draft states when pack data loads
  useEffect(() => {
    if (activePackData) {
      console.log('📝 Initializing draft states from pack data');
      setDraftIdentity(activePackData.identity || {});
      setDraftIngredients(activePackData.ingredients || activePackData.product_formulation?.ingredients || []);
      setDraftBenefits(activePackData.benefits?.product_benefits || []);
      setDraftSocial(activePackData.social || {});
      setDraftCommercial(activePackData.commercial || {});
      setDraftResearch(activePackData.research || {});
    }
  }, [activePackData]);
  
  // Change tracking
  const [hasChanges, setHasChanges] = useState({
    identity: false,
    ingredients: false,
    benefits: false,
    social: false,
    commercial: false,
    research: false
  });
  
  // Save states
  const [saving, setSaving] = useState({});
  const [lastSaved, setLastSaved] = useState({});
  
  // Generation states
  const [generating, setGenerating] = useState(null);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [editedBenefitText, setEditedBenefitText] = useState('');
  const [hoveredBenefit, setHoveredBenefit] = useState(null);
  const [importingStudy, setImportingStudy] = useState(false);
  const [studyDOI, setStudyDOI] = useState('');
  
  // Ambassador linking modal state
  const [showAmbassadorModal, setShowAmbassadorModal] = useState(false);
  
  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const markChanged = useCallback((section) => {
    setHasChanges(prev => ({ ...prev, [section]: true }));
  }, []);
  
  
  // Save section
  const saveSection = useCallback(async (section) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    
    try {
            const updates = {};
      
      // Prepare MongoDB-style flat updates
      switch (section) {
        case 'identity':
          Object.keys(draftIdentity).forEach(key => {
            updates[`identity.${key}`] = draftIdentity[key];
          });
          break;
        case 'ingredients':
          updates['ingredients'] = draftIngredients;
          break;
        case 'benefits':
          updates['benefits.product_benefits'] = draftBenefits;
          break;
        case 'social':
          Object.keys(draftSocial).forEach(key => {
            updates[`social.${key}`] = draftSocial[key];
          });
          break;
        case 'commercial':
          Object.keys(draftCommercial).forEach(key => {
            updates[`commercial.${key}`] = draftCommercial[key];
          });
          break;
        case 'research':
          Object.keys(draftResearch).forEach(key => {
            updates[`research.${key}`] = draftResearch[key];
          });
          break;
      }
      
      console.log('💾 [SAVE] Starting save for section:', section, 'pack:', packId);
      console.log('📤 [SAVE] Update payload:', updates);
      console.log('🔑 [SAVE] Has token:', !!secureStorage.getItem('nexus_access_token'));
      
      const url = `${BACKEND_URL}/api/v4/product-packs/${packId}?section=${section}`;
      console.log('🌐 [SAVE] Request URL:', url);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        },
        body: JSON.stringify(updates)
      });
      
      console.log('📡 [SAVE] Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const error = await response.json();
        console.error('❌ [SAVE] Error response:', error);
        
        // Provide helpful error messages
        if (response.status === 404) {
          throw new Error('Pack not found in V4 database. This pack may need to be migrated to Product Pack V4 first.');
        } else if (response.status === 403) {
          throw new Error('Permission denied. Management or Research role required.');
        } else {
          throw new Error(error.detail || 'Failed to save section');
        }
      }
      
      const data = await response.json();
      console.log('✅ [SAVE] Success response:', data);
      console.log('🎯 [SAVE] New version:', data.version);
      
      setLastSaved(prev => ({ ...prev, [section]: new Date().toLocaleTimeString() }));
      setHasChanges(prev => ({ ...prev, [section]: false }));
      toast.success(`✅ ${section} saved successfully!`, { duration: 3000 });
      
      if (onUpdate) onUpdate();
      
      // Refetch pack data to ensure frontend displays latest saved data
      console.log('🔄 [SAVE] Refetching pack data to display latest...');
      try {
        const refetchResponse = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}`, {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        });
        
        if (refetchResponse.ok) {
          const refreshedData = await refetchResponse.json();
          console.log('✅ [SAVE] Refetched data:', refreshedData);
          setLoadedPackData(refreshedData);
        }
      } catch (refetchError) {
        console.error('⚠️ [SAVE] Could not refetch data:', refetchError);
      }
      
    } catch (error) {
      console.error('❌ [SAVE] Exception:', error);
      toast.error(`Failed to save ${section}: ${error.message}`, {
        duration: 6000
      });
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  }, [draftIdentity, draftIngredients, draftBenefits, draftSocial, draftCommercial, draftResearch, packId, onUpdate, setLoadedPackData]);
  
  // Cancel section
  const cancelSection = useCallback((section) => {
    switch (section) {
      case 'identity':
        setDraftIdentity(packData?.identity || {});
        break;
      case 'ingredients':
        setDraftIngredients(packData?.ingredients || []);
        break;
      case 'benefits':
        setDraftBenefits(packData?.benefits?.product_benefits || []);
        break;
      case 'social':
        setDraftSocial(packData?.social || {});
        break;
      case 'commercial':
        setDraftCommercial(packData?.commercial || {});
        break;
      case 'research':
        setDraftResearch(packData?.research || {});
        break;
    }
    setHasChanges(prev => ({ ...prev, [section]: false }));
    toast.success('Changes discarded');
  }, [packData]);
  
  
  // Generate evidence with auto-migration
  const handleGenerateEvidence = useCallback(async (ingredientId, mode, setResolvedModeCallback) => {
    console.log('🎯 [AI GEN] Button clicked! ingredientId:', ingredientId, 'mode:', mode);
    console.log('🎯 [AI GEN] packId:', packId, 'userId:', userId);
    console.log('🎯 [AI GEN] packVersion:', packVersion);
    
    setGenerating(ingredientId);
    
    // Show prominent global loading popup
    const ingredientName = draftIngredients.find(ing => ing.id === ingredientId)?.common_name?.en || 
                          draftIngredients.find(ing => ing.id === ingredientId)?.common_name?.ms || 
                          'ingredient';
    
    toast.loading(
      `🤖 AI is analyzing ${ingredientName}...\n⏱️ This may take 10-15 seconds`, 
      { 
        id: 'ai-generate-loading',
        duration: Infinity, // Keep showing until dismissed
        style: {
          minWidth: '300px',
          fontSize: '14px'
        }
      }
    );
    
    // Check if pack needs migration
    if (packVersion === 'v3') {
      console.log('⚠️ [AI GEN] V3 pack detected, triggering auto-migration');
      toast.loading(
        '🔄 Migrating pack to V4 first...\n⏱️ Please wait', 
        { 
          id: 'ai-generate-loading',
          duration: Infinity,
          style: {
            minWidth: '300px',
            fontSize: '14px'
          }
        }
      );
      
      try {
        await migrateToV4();
        // After migration, retry generation
        toast.loading(
          `🤖 AI is analyzing ${ingredientName}...\n⏱️ This may take 10-15 seconds`, 
          { 
            id: 'ai-generate-loading',
            duration: Infinity,
            style: {
              minWidth: '300px',
              fontSize: '14px'
            }
          }
        );
      } catch (error) {
        console.error('❌ [AI GEN] Migration failed:', error);
        toast.dismiss('ai-generate-loading');
        setGenerating(null);
        return; // Stop if migration fails
      }
    }
    
    try {
            
      // Find ingredient to get narrative
      const ingredient = draftIngredients.find(ing => ing.id === ingredientId);
      const narrative = ingredient?.narrative || '';
      
      const requestPayload = {
        pack_id: packId,
        ingredient_id: ingredientId,
        user_id: userId,
        narrative: narrative  // Include optional narrative
      };
      
      const requestUrl = `${BACKEND_URL}/api/v4/product-packs/generate/evidence`;
      
      console.log('🚀 [AI GEN] Sending request:', {
        url: requestUrl,
        payload: requestPayload,
        packVersion,
        hasToken: !!secureStorage.getItem('nexus_access_token'),
        timestamp: new Date().toISOString()
      });
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        },
        body: JSON.stringify(requestPayload)
      });
      
      console.log('📡 [AI GEN] Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const error = await response.json();
        console.error('❌ [Generate Evidence] API Error:', error);
        
        // Handle 404 with migration option
        if (response.status === 404) {
          const shouldMigrate = window.confirm(
            'This pack is not found in V4 database. Would you like to migrate it now?'
          );
          if (shouldMigrate) {
            await migrateToV4();
            // Retry after migration
            toast.loading('Retrying generation...', { id: 'retry-generate' });
            return handleGenerateEvidence(ingredientId, mode, setResolvedModeCallback);
          }
          throw new Error('Pack not found. Please migrate to V4 first.');
        } else if (response.status === 403) {
          throw new Error('Permission denied. Management or Research role required.');
        } else if (response.status === 500) {
          throw new Error(`Server error: ${error.detail || 'Unknown error'}`);
        } else {
          throw new Error(error.detail || 'Failed to generate evidence');
        }
      }
      
      const data = await response.json();
      console.log('✅ [AI GEN] Success! Received data:', {
        compounds: data.active_compounds?.length,
        evidence: data.evidence?.length,
        benefits: data.suggested_benefits?.length,
        resolved_mode: data.provenance?.resolved_mode,
        auto_detected: data.provenance?.auto_detected,
        fullResponse: data
      });
      
      // Extract resolved mode from provenance
      const resolved_mode = data.provenance?.resolved_mode;
      if (resolved_mode && setResolvedModeCallback) {
        console.log('🎯 [AI GEN] Setting resolved mode:', resolved_mode);
        setResolvedModeCallback(resolved_mode);
      }
      
      // Update ingredient with compounds and evidence
      console.log('📝 [AI GEN] Updating ingredient with AI data...');
      
      // Capture updated ingredients for saving
      let updatedIngredientsForSave = null;
      
      setDraftIngredients(prev => {
        const updated = prev.map(ing => {
          if (ing.id === ingredientId) {
            console.log('✏️ [AI GEN] Found ingredient to update:', ing.common_name);
            return {
              ...ing,
              active_compounds: data.active_compounds, // Replace, don't append
              ai_generated_evidence: data.evidence,     // Replace, don't append
              linked_benefit_ids: data.suggested_benefits.map(b => b.id)
            };
          }
          return ing;
        });
        updatedIngredientsForSave = updated;
        return updated;
      });
      
      // Add suggested benefits
      setDraftBenefits(prev => [...prev, ...data.suggested_benefits]);
      
      // Show success message with mode info
      const modeEmoji = {
        'scientific': '🧠',
        'emotional': '💝',
        'hybrid': '⚖️'
      }[resolved_mode] || '✨';
      
      const modeText = data.provenance?.auto_detected 
        ? `AI chose: ${resolved_mode}` 
        : resolved_mode;
      
      console.log('✅ [AI GEN] Successfully updated ingredient!');
      console.log('💾 [AI GEN] Auto-saving ingredients immediately to preserve compounds & evidence...');
      
      // CRITICAL: Auto-save ingredients IMMEDIATELY after AI generation
      // This prevents data loss when user saves Section 3 (benefits) later
      try {
                
        console.log('📤 [AI GEN] Sending updated ingredients to backend:', {
          count: updatedIngredientsForSave.length,
          ingredientId: ingredientId,
          hasCompounds: updatedIngredientsForSave.some(ing => ing.active_compounds?.length > 0),
          hasEvidence: updatedIngredientsForSave.some(ing => ing.ai_generated_evidence?.length > 0)
        });
        
        const saveResponse = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}?section=ingredients`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          },
          body: JSON.stringify({
            'ingredients': updatedIngredientsForSave
          })
        });
        
        if (!saveResponse.ok) {
          const error = await saveResponse.json();
          throw new Error(error.detail || 'Failed to auto-save ingredients');
        }
        
        const saveData = await saveResponse.json();
        console.log('✅ [AI GEN] Ingredients auto-saved successfully:', saveData);
        setLastSaved(prev => ({ ...prev, ingredients: new Date().toLocaleTimeString() }));
        
        // Clear "unsaved changes" flag for ingredients since we just saved
        setHasChanges(prev => ({ ...prev, ingredients: false }));
        
      } catch (saveError) {
        console.error('❌ [AI GEN] Auto-save failed:', saveError);
        toast.error('⚠️ AI generated successfully but failed to auto-save. Please save Section 2 manually!', { duration: 8000 });
        // Still mark as changed so user can manually save
        markChanged('ingredients');
      }
      
      // Mark benefits as changed (user needs to accept and save)
      markChanged('benefits');
      
      // Dismiss loading toast
      toast.dismiss('ai-generate-loading');
      toast.dismiss('generate-after-migrate');
      toast.dismiss('retry-generate');
      
      // Show success with detailed breakdown
      toast.success(
        `${modeEmoji} AI Analysis Complete!\n✓ ${data.active_compounds.length} Active Compounds\n✓ ${data.evidence.length} Evidence Entries\n✓ ${data.suggested_benefits.length} Benefits\n\n⚠️ SAVE Section 2 & 3 to keep data!\n\nMode: ${modeText}`,
        { 
          duration: 8000, 
          id: 'generate-success',
          style: {
            minWidth: '340px',
            fontSize: '14px',
            whiteSpace: 'pre-line'
          }
        }
      );
      
    } catch (error) {
      console.error('❌ [AI GEN] Exception caught:', error);
      console.error('❌ [AI GEN] Error stack:', error.stack);
      
      // Dismiss all loading toasts
      toast.dismiss('ai-generate-loading');
      toast.dismiss('generate-after-migrate');
      toast.dismiss('retry-generate');
      
      // Show detailed error
      toast.error(
        `❌ AI Generation Failed\n\nError: ${error.message}\n\nPlease try again or contact support if the issue persists.`,
        {
          duration: 8000,
          id: 'generate-error',
          style: {
            minWidth: '320px',
            fontSize: '14px',
            whiteSpace: 'pre-line'
          }
        }
      );
    } finally {
      console.log('🏁 [AI GEN] Cleaning up, setting generating to null');
      setGenerating(null);
    }
  }, [packId, userId, packVersion, migrateToV4, markChanged]);
  
  // Import research study
  const handleImportStudy = useCallback(async () => {
    if (!studyDOI.trim()) {
      toast.error('Please enter a DOI');
      return;
    }
    
    setImportingStudy(true);
    
    try {
            const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/research/import-study`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        },
        body: JSON.stringify({
          pack_id: packId,
          doi: studyDOI,
          user_id: userId
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to import study');
      }
      
      const data = await response.json();
      
      // Add study to research layer
      setDraftResearch(prev => ({
        ...prev,
        research_studies: [
          ...(prev.research_studies || []),
          data.study
        ]
      }));
      
      markChanged('research');
      setStudyDOI('');
      toast.success('✅ Research study imported successfully!');
    } catch (error) {
      console.error('Study import error:', error);
      toast.error(`❌ ${error.message}`);
    } finally {
      setImportingStudy(false);
    }
  }, [studyDOI, packId, userId]);
  
  // Benefits handlers
  const acceptBenefit = async (benefitId) => {
    let updatedBenefitsToSave = null;
    
    // Update the state and capture the updated benefits
    setDraftBenefits(prev => {
      const updated = prev.map(b => 
        b.id === benefitId ? { ...b, accepted: true } : b
      );
      updatedBenefitsToSave = updated;
      return updated;
    });
    
    // Auto-save after accepting benefit
    try {
            
      console.log('💾 [Accept Benefit] Saving benefit:', benefitId);
      
      const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}?section=benefits`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        },
        body: JSON.stringify({
          'benefits.product_benefits': updatedBenefitsToSave
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save benefit');
      }
      
      console.log('✅ [Accept Benefit] Saved successfully');
      toast.success('✅ Benefit accepted and saved!', { duration: 2000 });
      
      // Update last saved timestamp
      setLastSaved(prev => ({ ...prev, benefits: new Date().toLocaleTimeString() }));
      
    } catch (error) {
      console.error('❌ [Accept Benefit] Failed:', error);
      toast.error('Benefit accepted but not saved. Please click Save manually.');
    }
  };
  
  const acceptAllBenefits = async () => {
    let updatedBenefitsToSave = null;
    
    // Update the state and capture the updated benefits
    setDraftBenefits(prev => {
      const updated = prev.map(b => 
        b.ai_suggested ? { ...b, accepted: true } : b
      );
      updatedBenefitsToSave = updated;
      return updated;
    });
    
    // Auto-save after accepting all benefits
    try {
            
      const aiSuggestionsCount = updatedBenefitsToSave.filter(b => b.ai_suggested).length;
      console.log(`💾 [Accept All] Saving ${aiSuggestionsCount} AI suggestions`);
      
      const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}?section=benefits`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
        },
        body: JSON.stringify({
          'benefits.product_benefits': updatedBenefitsToSave
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save benefits');
      }
      
      console.log('✅ [Accept All] Saved successfully');
      toast.success(`✅ All ${aiSuggestionsCount} AI suggestions accepted and saved!`, { duration: 2000 });
      
      // Update last saved timestamp
      setLastSaved(prev => ({ ...prev, benefits: new Date().toLocaleTimeString() }));
      
    } catch (error) {
      console.error('❌ [Accept All] Failed:', error);
      toast.error('Benefits accepted but not saved. Please click Save manually.');
    }
  };
  
  const deleteBenefit = (benefitId) => {
    if (window.confirm('Delete this benefit?')) {
      setDraftBenefits(prev => prev.filter(b => b.id !== benefitId));
      markChanged('benefits');
    }
  };
  
  const startEditBenefit = (benefitId) => {
    const benefit = draftBenefits.find(b => b.id === benefitId);
    if (benefit) {
      setEditingBenefit(benefitId);
      setEditedBenefitText(benefit.title);
    }
  };
  
  const saveEditedBenefit = () => {
    if (editingBenefit && editedBenefitText.trim()) {
      setDraftBenefits(prev => prev.map(b =>
        b.id === editingBenefit ? { ...b, title: editedBenefitText.trim() } : b
      ));
      markChanged('benefits');
      setEditingBenefit(null);
      setEditedBenefitText('');
    }
  };
  
  // Handler for when ambassador is linked from modal
  const handleAmbassadorLinked = async () => {
    // Reload the pack data to get updated ambassadors with persona snapshots
    if (onUpdate) {
      await onUpdate(); // Trigger parent reload
    }
    toast.success('✅ Ambassador linked! Save Section 4 to persist changes.');
  };
  
  const aiSuggestions = draftBenefits.filter(b => b.ai_suggested && !b.accepted);
  const acceptedBenefits = draftBenefits.filter(b => b.accepted || !b.ai_suggested);
  
  // Group benefits by ingredient for better organization
  const groupBenefitsByIngredient = (benefits) => {
    const grouped = {};
    benefits.forEach(benefit => {
      const ingredientName = benefit.source_ingredient_name || 'General';
      if (!grouped[ingredientName]) {
        grouped[ingredientName] = [];
      }
      grouped[ingredientName].push(benefit);
    });
    return grouped;
  };
  
  const groupedAISuggestions = groupBenefitsByIngredient(aiSuggestions);
  const groupedAcceptedBenefits = groupBenefitsByIngredient(acceptedBenefits);
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pack data...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4 pb-24 overflow-y-auto" style={{ minHeight: '100vh', maxHeight: '100vh' }}>
      {/* Toast Notifications - Matches DynamicLoader CI */}
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#FFFFFF',
            color: '#1F2937',              // Tailwind gray-800 (matches DynamicLoader)
            fontSize: '16px',              // Matches DynamicLoader text size
            fontWeight: '600',             // Semibold like DynamicLoader
            padding: '24px',               // p-6 equivalent
            borderRadius: '16px',          // rounded-2xl (matches DynamicLoader)
            border: 'none',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // shadow-2xl (matches DynamicLoader)
            maxWidth: '400px',
            minWidth: '320px',
          },
          success: {
            duration: 4000,
            style: {
              background: '#FFFFFF',
              color: '#1F2937',
            },
            iconTheme: {
              primary: '#16A34A',
              secondary: '#FFFFFF',
            },
          },
          error: {
            duration: 6000,
            style: {
              background: '#FFFFFF',
              color: '#1F2937',
            },
            iconTheme: {
              primary: '#DC2626',
              secondary: '#FFFFFF',
            },
          },
          loading: {
            duration: Infinity,
            style: {
              background: '#FFFFFF',
              color: '#1F2937',
            },
            iconTheme: {
              primary: '#2563EB',  // Blue-600 (matches DynamicLoader spinner)
              secondary: '#FFFFFF',
            },
          },
        }}
      />
      
      {/* Ambassador Linking Modal */}
      <AmbassadorLinkingModal
        isOpen={showAmbassadorModal}
        onClose={() => setShowAmbassadorModal(false)}
        productPackId={packId}
        onAmbassadorLinked={handleAmbassadorLinked}
      />
      
      {/* Back Button - Added for Issue #3 */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium text-sm"
        >
          <span>←</span>
          <span>Back to Knowledge Packs</span>
        </button>
      </div>
      
      {/* Product Content Introduction Banner */}
      <div style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '8px',
        color: '#fff',
        marginBottom: '12px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>📦</span>
          <span>Product Content - Maklumat Produk</span>
        </div>
        <div style={{ fontSize: '12px', lineHeight: '1.5', opacity: 0.95 }}>
          <strong>Apa yang perlu diisi:</strong> Maklumat lengkap tentang produk.
          <br/>
          <strong>Kenapa penting:</strong> AI guna untuk Content Generation, SOA Drill, Format Suggestion & Agent Context.
          <br/>
          💡 <strong>Tip:</strong> Isi dengan lengkap untuk hasil AI yang lebih accurate!
        </div>
      </div>

      {/* Section 1: Product Identity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          type="button"
          onClick={() => toggleSection('identity')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">1️⃣ Product Identity</h3>
          <span className="text-gray-500">{openSections.identity ? '▼' : '▶'}</span>
        </button>
        
        {openSections.identity && (
          <IdentitySection
            identity={draftIdentity}
            onUpdate={setDraftIdentity}
            onMarkChanged={() => markChanged('identity')}
            hasChanges={hasChanges.identity}
            lastSaved={lastSaved.identity}
            saving={saving.identity}
            onSave={() => saveSection('identity')}
            onCancel={() => cancelSection('identity')}
            t={t}
            help={help}
            language={language}
          />
        )}
      </div>
      
      {/* Section 2: Ingredient Intelligence */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          type="button"
          onClick={() => toggleSection('ingredients')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">2️⃣ Ingredient Intelligence</h3>
          <span className="text-gray-500">{openSections.ingredients ? '▼' : '▶'}</span>
        </button>
        
        {openSections.ingredients && (
          <div className="p-4 border-t border-gray-200 relative">
            <p className="text-sm text-gray-600 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              💡 <strong>Tip:</strong> Add your product ingredients here first. Then go to <strong>Section 3 (Benefit & Proof)</strong> to generate evidence and benefits with AI.
            </p>
            
            <div className="space-y-4 mb-4">
              {draftIngredients.map((ingredient, i) => (
                <IngredientItemSimple
                  key={ingredient.id}
                  ingredient={ingredient}
                  onUpdate={(updated) => {
                    const newIngredients = [...draftIngredients];
                    newIngredients[i] = updated;
                    setDraftIngredients(newIngredients);
                    markChanged('ingredients');
                  }}
                  onRemove={() => {
                    const ingredientToRemove = draftIngredients[i];
                    const ingredientName = ingredientToRemove.common_name?.en || ingredientToRemove.common_name?.ms || 'this ingredient';
                    
                    if (window.confirm(`Remove ${ingredientName}? This will also remove all associated benefits, compounds, and evidence.`)) {
                      // Get ingredient ID for filtering benefits
                      const ingredientId = ingredientToRemove.id;
                      const ingredientSourceName = ingredientToRemove.common_name?.en || ingredientToRemove.common_name?.ms || '';
                      
                      let removedBenefitsCount = 0;
                      
                      // Remove ingredient
                      setDraftIngredients(prev => prev.filter((_, idx) => idx !== i));
                      
                      // Remove all benefits associated with this ingredient
                      setDraftBenefits(prev => {
                        const filtered = prev.filter(benefit => {
                          // Check if benefit is linked to this ingredient via source_ingredient_name or id
                          const isLinkedByName = benefit.source_ingredient_name === ingredientSourceName;
                          const isLinkedById = benefit.source_ingredient_id === ingredientId;
                          const isInLinkedIds = benefit.supported_by_ingredient_ids?.includes(ingredientId);
                          
                          // Keep benefit only if it's NOT linked to this ingredient
                          return !(isLinkedByName || isLinkedById || isInLinkedIds);
                        });
                        
                        removedBenefitsCount = prev.length - filtered.length;
                        
                        console.log('🗑️ [Ingredient Remove] Removed benefits for:', ingredientName, {
                          before: prev.length,
                          after: filtered.length,
                          removed: removedBenefitsCount
                        });
                        
                        return filtered;
                      });
                      
                      markChanged('ingredients');
                      markChanged('benefits');
                      
                      if (removedBenefitsCount > 0) {
                        toast.success(`Removed ${ingredientName} and ${removedBenefitsCount} associated benefit(s)`);
                      } else {
                        toast.success(`Removed ${ingredientName}`);
                      }
                    }
                  }}
                />
              ))}
              <button
                type="button"
                onClick={() => {
                  const newIngredient = {
                    id: `ing_${Date.now()}`,
                    common_name: { en: '', ms: '' },
                    scientific_name: '',
                    origin: null,
                    active_compounds: [],
                    ai_generated_evidence: [],
                    linked_benefit_ids: [],
                    linked_study_ids: []
                  };
                  setDraftIngredients(prev => [...prev, newIngredient]);
                  markChanged('ingredients');
                }}
                className="w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg border border-dashed border-blue-300"
              >
                + Add Ingredient
              </button>
            </div>
            
            <SectionSaveBar
              section="ingredients"
              hasChanges={hasChanges.ingredients}
              lastSaved={lastSaved.ingredients}
              saving={saving.ingredients}
              onSave={() => saveSection('ingredients')}
              onCancel={() => cancelSection('ingredients')}
            />
          </div>
        )}
      </div>
      
      {/* Section 3: Benefit & Proof */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          type="button"
          onClick={() => toggleSection('benefits')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">3️⃣ Benefit & Proof</h3>
          <span className="text-gray-500">{openSections.benefits ? '▼' : '▶'}</span>
        </button>
        
        {openSections.benefits && (
          <div className="p-4 border-t border-gray-200 relative">
            <p className="text-sm text-gray-600 mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
              🔬 <strong>Evidence Hub:</strong> Generate AI-powered evidence for each ingredient. The AI will create active compounds, scientific evidence, and suggested benefits all in one place!
            </p>
            
            {/* Ingredient Evidence Cards */}
            {draftIngredients.length > 0 ? (
              <div className="space-y-4 mb-4">
                {draftIngredients.map((ingredient) => (
                  <IngredientEvidenceCard
                    key={ingredient.id}
                    ingredient={ingredient}
                    onUpdate={(updated) => {
                      const newIngredients = draftIngredients.map(ing => 
                        ing.id === ingredient.id ? updated : ing
                      );
                      setDraftIngredients(newIngredients);
                      markChanged('ingredients');
                    }}
                    onGenerateEvidence={handleGenerateEvidence}
                    generating={generating}
                    benefits={draftBenefits}
                    onAcceptBenefit={acceptBenefit}
                    onAcceptAllBenefits={async (ingredientId) => {
                      // Accept all benefits for this specific ingredient
                      const ingredientBenefits = draftBenefits.filter(b => 
                        b.ai_suggested && 
                        !b.accepted && 
                        (b.source_ingredient_id === ingredientId || 
                         b.source_ingredient_name === (ingredient.common_name?.en || ingredient.common_name?.ms))
                      );
                      
                      if (ingredientBenefits.length > 0) {
                        let updatedBenefitsToSave = null;
                        
                        // Update state and capture updated benefits
                        setDraftBenefits(prev => {
                          const updated = prev.map(b => {
                            const shouldAccept = ingredientBenefits.some(ib => ib.id === b.id);
                            return shouldAccept ? { ...b, accepted: true } : b;
                          });
                          updatedBenefitsToSave = updated;
                          return updated;
                        });
                        
                        // Auto-save directly to API (same pattern as acceptBenefit)
                        try {
                                                    
                          const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}?section=benefits`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                            },
                            body: JSON.stringify({
                              'benefits.product_benefits': updatedBenefitsToSave
                            })
                          });
                          
                          if (!response.ok) {
                            const error = await response.json();
                            throw new Error(error.detail || 'Failed to save benefits');
                          }
                          
                          console.log('✅ [Accept All - Ingredient] Saved successfully');
                          toast.success(`✅ Accepted and saved ${ingredientBenefits.length} benefit(s)!`);
                          
                          // Update last saved timestamp
                          setLastSaved(prev => ({ ...prev, benefits: new Date().toLocaleTimeString() }));
                          
                        } catch (error) {
                          console.error('❌ [Accept All - Ingredient] Failed:', error);
                          toast.error(`Failed to save benefits: ${error.message}`);
                        }
                      }
                    }}
                    onDeleteBenefit={deleteBenefit}
                    onEditBenefit={startEditBenefit}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-lg font-semibold mb-2">📝 No Ingredients Yet</p>
                <p className="text-sm">Add ingredients in Section 2 first, then come back here to generate evidence!</p>
              </div>
            )}
            
            {/* Old benefit display code removed - now handled by IngredientEvidenceCard */}
            
            <SectionSaveBar
              section="benefits"
              hasChanges={hasChanges.benefits}
              lastSaved={lastSaved.benefits}
              saving={saving.benefits}
              onSave={() => saveSection('benefits')}
              onCancel={() => cancelSection('benefits')}
            />
            
            {/* Edit Modal */}
            {editingBenefit && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full mx-4">
                  <h4 className="text-lg font-bold mb-3">Edit Benefit</h4>
                  <input
                    type="text"
                    value={editedBenefitText}
                    onChange={(e) => setEditedBenefitText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingBenefit(null);
                        setEditedBenefitText('');
                      }}
                      className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveEditedBenefit}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Section 4: Social & Emotional */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          type="button"
          onClick={() => toggleSection('social')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">4️⃣ Social & Emotional</h3>
          <span className="text-gray-500">{openSections.social ? '▼' : '▶'}</span>
        </button>
        
        {openSections.social && (
          <div className="p-4 border-t border-gray-200 relative">
            <div className="space-y-6 mb-4">
              {/* Testimonials */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3">Customer Testimonials</h4>
                <div className="space-y-3">
                  {draftSocial.testimonials?.map((testimonial, i) => (
                    <TestimonialItem
                      key={testimonial.id || i}
                      testimonial={testimonial}
                      index={i}
                      onUpdate={(updated) => {
                        const newTestimonials = [...(draftSocial.testimonials || [])];
                        newTestimonials[i] = updated;
                        setDraftSocial(prev => ({ ...prev, testimonials: newTestimonials }));
                        markChanged('social');
                      }}
                      onRemove={() => {
                        const newTestimonials = draftSocial.testimonials.filter((_, idx) => idx !== i);
                        setDraftSocial(prev => ({ ...prev, testimonials: newTestimonials }));
                        markChanged('social');
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newTestimonial = {
                        id: `test_${Date.now()}`,
                        user_name: '',
                        profile: '',
                        feedback_text: '',
                        rating: 5,
                        duration_used: '',
                        verified: false
                      };
                      setDraftSocial(prev => ({
                        ...prev,
                        testimonials: [...(prev.testimonials || []), newTestimonial]
                      }));
                      markChanged('social');
                    }}
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg border border-dashed border-blue-300"
                  >
                    + Add Testimonial
                  </button>
                </div>
              </div>

              {/* Ambassadors */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-md font-semibold text-gray-800">Brand Ambassadors</h4>
                  <button
                    type="button"
                    onClick={() => setShowAmbassadorModal(true)}
                    className="px-3 py-1.5 text-xs font-semibold bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all"
                  >
                    🎭 Link Ambassador Pack
                  </button>
                </div>
                <div className="space-y-3">
                  {draftSocial.ambassadors?.map((ambassador, i) => (
                    <AmbassadorItem
                      key={ambassador.id || i}
                      ambassador={ambassador}
                      index={i}
                      onUpdate={(updated) => {
                        const newAmbassadors = [...(draftSocial.ambassadors || [])];
                        newAmbassadors[i] = updated;
                        setDraftSocial(prev => ({ ...prev, ambassadors: newAmbassadors }));
                        markChanged('social');
                      }}
                      onRemove={() => {
                        const newAmbassadors = draftSocial.ambassadors.filter((_, idx) => idx !== i);
                        setDraftSocial(prev => ({ ...prev, ambassadors: newAmbassadors }));
                        markChanged('social');
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newAmbassador = {
                        id: `amb_${Date.now()}`,
                        name: '',
                        type: 'Influencer',
                        social_handle: '',
                        endorsement_text: ''
                      };
                      setDraftSocial(prev => ({
                        ...prev,
                        ambassadors: [...(prev.ambassadors || []), newAmbassador]
                      }));
                      markChanged('social');
                    }}
                    className="w-full px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg border border-dashed border-purple-300"
                  >
                    + Add Ambassador (Manual)
                  </button>
                </div>
              </div>

              {/* Emotional Triggers */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3">Emotional Triggers</h4>
                <div className="flex flex-wrap gap-2">
                  {['Hope', 'Trust', 'Relief', 'Pride', 'Joy', 'Confidence', 'Security'].map(trigger => (
                    <button
                      key={trigger}
                      type="button"
                      onClick={() => {
                        const triggers = draftSocial.customer_emotional_triggers || [];
                        const newTriggers = triggers.includes(trigger)
                          ? triggers.filter(t => t !== trigger)
                          : [...triggers, trigger];
                        setDraftSocial(prev => ({ ...prev, customer_emotional_triggers: newTriggers }));
                        markChanged('social');
                      }}
                      className={`px-3 py-2 text-sm rounded-full transition-all ${
                        (draftSocial.customer_emotional_triggers || []).includes(trigger)
                          ? 'bg-pink-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {trigger}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <SectionSaveBar
              section="social"
              hasChanges={hasChanges.social}
              lastSaved={lastSaved.social}
              saving={saving.social}
              onSave={() => saveSection('social')}
              onCancel={() => cancelSection('social')}
            />
          </div>
        )}
      </div>
      
      {/* Section 5: Commercial */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          type="button"
          onClick={() => toggleSection('commercial')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">5️⃣ Commercial</h3>
          <span className="text-gray-500">{openSections.commercial ? '▼' : '▶'}</span>
        </button>
        
        {openSections.commercial && (
          <div className="p-4 border-t border-gray-200 relative">
            <div className="space-y-6 mb-4">
              {/* Pricing */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3">Pricing Variants</h4>
                <div className="space-y-3">
                  {draftCommercial.pricing?.map((price, i) => (
                    <div key={price.id || i} className="p-4 border border-green-200 rounded-lg bg-green-50">
                      <input
                        type="text"
                        value={price.variant_name || ''}
                        onChange={(e) => {
                          const newPricing = [...(draftCommercial.pricing || [])];
                          newPricing[i] = { ...price, variant_name: e.target.value };
                          setDraftCommercial(prev => ({ ...prev, pricing: newPricing }));
                          markChanged('commercial');
                        }}
                        placeholder="Variant Name (e.g., Single Box, Bundle 3)"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 mb-2"
                      />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Unit Price (MYR)</label>
                          <input
                            type="number"
                            value={price.unit_price || ''}
                            onChange={(e) => {
                              const newPricing = [...(draftCommercial.pricing || [])];
                              newPricing[i] = { ...price, unit_price: parseFloat(e.target.value) || 0 };
                              setDraftCommercial(prev => ({ ...prev, pricing: newPricing }));
                              markChanged('commercial');
                            }}
                            placeholder="99.00"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Bundle Price</label>
                          <input
                            type="number"
                            value={price.bundle_price || ''}
                            onChange={(e) => {
                              const newPricing = [...(draftCommercial.pricing || [])];
                              newPricing[i] = { ...price, bundle_price: parseFloat(e.target.value) || null };
                              setDraftCommercial(prev => ({ ...prev, pricing: newPricing }));
                              markChanged('commercial');
                            }}
                            placeholder="270.00"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Discount %</label>
                          <input
                            type="number"
                            value={price.discount_percentage || ''}
                            onChange={(e) => {
                              const newPricing = [...(draftCommercial.pricing || [])];
                              newPricing[i] = { ...price, discount_percentage: parseFloat(e.target.value) || null };
                              setDraftCommercial(prev => ({ ...prev, pricing: newPricing }));
                              markChanged('commercial');
                            }}
                            placeholder="10"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Promo Code</label>
                          <input
                            type="text"
                            value={price.promo_code || ''}
                            onChange={(e) => {
                              const newPricing = [...(draftCommercial.pricing || [])];
                              newPricing[i] = { ...price, promo_code: e.target.value };
                              setDraftCommercial(prev => ({ ...prev, pricing: newPricing }));
                              markChanged('commercial');
                            }}
                            placeholder="SAVE10"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newPricing = draftCommercial.pricing.filter((_, idx) => idx !== i);
                          setDraftCommercial(prev => ({ ...prev, pricing: newPricing }));
                          markChanged('commercial');
                        }}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Remove Variant
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newPrice = {
                        id: `price_${Date.now()}`,
                        variant_name: '',
                        unit_price: 0,
                        currency: 'MYR'
                      };
                      setDraftCommercial(prev => ({
                        ...prev,
                        pricing: [...(prev.pricing || []), newPrice]
                      }));
                      markChanged('commercial');
                    }}
                    className="w-full px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg border border-dashed border-green-300"
                  >
                    + Add Pricing Variant
                  </button>
                </div>
              </div>

              {/* Target Audience */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3">Target Audience</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Demographic</label>
                    <input
                      type="text"
                      value={draftCommercial.target_audience?.demographic || ''}
                      onChange={(e) => {
                        setDraftCommercial(prev => ({
                          ...prev,
                          target_audience: { ...prev.target_audience, demographic: e.target.value }
                        }));
                        markChanged('commercial');
                      }}
                      placeholder="e.g., Working professionals, mothers"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Age Range</label>
                    <input
                      type="text"
                      value={draftCommercial.target_audience?.age_range || ''}
                      onChange={(e) => {
                        setDraftCommercial(prev => ({
                          ...prev,
                          target_audience: { ...prev.target_audience, age_range: e.target.value }
                        }));
                        markChanged('commercial');
                      }}
                      placeholder="e.g., 25-45"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-xs text-gray-600 mb-1">Gender</label>
                  <select
                    value={draftCommercial.target_audience?.gender || 'All'}
                    onChange={(e) => {
                      setDraftCommercial(prev => ({
                        ...prev,
                        target_audience: { ...prev.target_audience, gender: e.target.value }
                      }));
                      markChanged('commercial');
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="All">All</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="block text-xs text-gray-600 mb-1">Lifestyle</label>
                  <DebouncedTextarea
                    initialValue={draftCommercial.target_audience?.lifestyle || ''}
                    onChangeComplete={(value) => {
                      setDraftCommercial(prev => ({
                        ...prev,
                        target_audience: { ...prev.target_audience, lifestyle: value }
                      }));
                      markChanged('commercial');
                    }}
                    placeholder="Describe target audience lifestyle"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={2}
                  />
                </div>
              </div>

              {/* Sales Channels */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3">Sales Channels</h4>
                <div className="flex flex-wrap gap-2">
                  {['TikTok', 'Shopee', 'LP', 'Pharmacy', 'WhatsApp', 'Facebook', 'Instagram'].map(channel => (
                    <button
                      key={channel}
                      type="button"
                      onClick={() => {
                        const channels = draftCommercial.sales_channels || [];
                        const newChannels = channels.includes(channel)
                          ? channels.filter(c => c !== channel)
                          : [...channels, channel];
                        setDraftCommercial(prev => ({ ...prev, sales_channels: newChannels }));
                        markChanged('commercial');
                      }}
                      className={`px-3 py-2 text-sm rounded-full transition-all ${
                        (draftCommercial.sales_channels || []).includes(channel)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {channel}
                    </button>
                  ))}
                </div>
              </div>

              {/* Marketing Messages */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3">Marketing Messages</h4>
                <div className="space-y-3">
                  {draftCommercial.marketing_messaging?.map((message, i) => (
                    <div key={message.id || i} className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                        <select
                          value={message.type || 'Slogan'}
                          onChange={(e) => {
                            const newMessages = [...(draftCommercial.marketing_messaging || [])];
                            newMessages[i] = { ...message, type: e.target.value };
                            setDraftCommercial(prev => ({ ...prev, marketing_messaging: newMessages }));
                            markChanged('commercial');
                          }}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Slogan">Slogan</option>
                          <option value="CTA">CTA</option>
                          <option value="Positioning">Positioning</option>
                          <option value="Tagline">Tagline</option>
                          <option value="Headline">Headline</option>
                        </select>
                        <select
                          value={message.channel || ''}
                          onChange={(e) => {
                            const newMessages = [...(draftCommercial.marketing_messaging || [])];
                            newMessages[i] = { ...message, channel: e.target.value };
                            setDraftCommercial(prev => ({ ...prev, marketing_messaging: newMessages }));
                            markChanged('commercial');
                          }}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">All Channels</option>
                          <option value="TikTok">TikTok</option>
                          <option value="Shopee">Shopee</option>
                          <option value="LP">Landing Page</option>
                          <option value="Pharmacy">Pharmacy</option>
                          <option value="WhatsApp">WhatsApp</option>
                        </select>
                      </div>
                      <input
                        type="text"
                        value={message.text || ''}
                        onChange={(e) => {
                          const newMessages = [...(draftCommercial.marketing_messaging || [])];
                          newMessages[i] = { ...message, text: e.target.value };
                          setDraftCommercial(prev => ({ ...prev, marketing_messaging: newMessages }));
                          markChanged('commercial');
                        }}
                        placeholder="Message text"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newMessages = draftCommercial.marketing_messaging.filter((_, idx) => idx !== i);
                          setDraftCommercial(prev => ({ ...prev, marketing_messaging: newMessages }));
                          markChanged('commercial');
                        }}
                        className="text-sm text-red-600 hover:underline mt-2"
                      >
                        Remove Message
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newMessage = {
                        id: `msg_${Date.now()}`,
                        type: 'Slogan',
                        text: '',
                        ai_generated: false
                      };
                      setDraftCommercial(prev => ({
                        ...prev,
                        marketing_messaging: [...(prev.marketing_messaging || []), newMessage]
                      }));
                      markChanged('commercial');
                    }}
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg border border-dashed border-blue-300"
                  >
                    + Add Marketing Message
                  </button>
                </div>
              </div>
            </div>
            
            <SectionSaveBar
              section="commercial"
              hasChanges={hasChanges.commercial}
              lastSaved={lastSaved.commercial}
              saving={saving.commercial}
              onSave={() => saveSection('commercial')}
              onCancel={() => cancelSection('commercial')}
            />
          </div>
        )}
      </div>
      
      {/* Section 6: Research & Provenance */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          type="button"
          onClick={() => toggleSection('research')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">6️⃣ Research & Provenance</h3>
          <span className="text-gray-500">{openSections.research ? '▼' : '▶'}</span>
        </button>
        
        {openSections.research && (
          <div className="p-4 border-t border-gray-200 relative">
            <div className="mb-4">
              <h4 className="text-md font-semibold text-gray-800 mb-3">Import Research Study</h4>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={studyDOI}
                  onChange={(e) => setStudyDOI(e.target.value)}
                  placeholder="Enter DOI (e.g., 10.1234/example)"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={handleImportStudy}
                  disabled={importingStudy}
                  className="px-4 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {importingStudy ? '⏳ Importing...' : '📚 Import'}
                </button>
              </div>
              
              <div className="space-y-3">
                {draftResearch.research_studies?.map((study, i) => (
                  <ResearchStudyCard
                    key={study.id || i}
                    study={study}
                    onLink={(studyId) => {
                      // Link study to benefits
                      toast.info('Link study to benefits - Coming soon');
                    }}
                    onRemove={(studyId) => {
                      const newStudies = draftResearch.research_studies.filter(s => s.id !== studyId);
                      setDraftResearch(prev => ({ ...prev, research_studies: newStudies }));
                      markChanged('research');
                    }}
                  />
                ))}
              </div>
              
              {(!draftResearch.research_studies || draftResearch.research_studies.length === 0) && (
                <p className="text-sm text-gray-500 italic">No research studies added yet</p>
              )}
            </div>
            
            <SectionSaveBar
              section="research"
              hasChanges={hasChanges.research}
              lastSaved={lastSaved.research}
              saving={saving.research}
              onSave={() => saveSection('research')}
              onCancel={() => cancelSection('research')}
            />
          </div>
        )}
      </div>
      
      {/* Section 7: System & Versioning */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          type="button"
          onClick={() => toggleSection('system')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">7️⃣ System & Versioning</h3>
          <span className="text-gray-500">{openSections.system ? '▼' : '▶'}</span>
        </button>
        
        {openSections.system && (
          <div className="p-4 border-t border-gray-200">
            <div className="space-y-4">
              {/* Status Badge & Publish Button */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-sm font-semibold text-gray-700">Status:</div>
                  <span className={`px-3 py-1 text-sm rounded-full ${
                    activePackData?.status === 'draft'
                      ? 'bg-yellow-100 text-yellow-700'
                      : activePackData?.status === 'published'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {activePackData?.status === 'draft' ? '📝 Draft' : activePackData?.status === 'published' ? '✅ Published' : '📦 Archived'}
                  </span>
                  <div className="text-xs text-gray-500">
                    Version {activePackData?.system?.current_version || 1}
                  </div>
                </div>
                
                {/* Publish/Unpublish Buttons */}
                <div className="flex gap-2 flex-wrap">
                  {activePackData?.status === 'draft' && (
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to publish this product pack? It will be visible to all users.')) {
                          try {
                                                        const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                              },
                              body: JSON.stringify({ status: 'published' })
                            });
                            
                            if (response.ok) {
                              alert('✅ Product pack published successfully!');
                              window.location.reload();
                            } else {
                              throw new Error('Failed to publish');
                            }
                          } catch (error) {
                            alert('❌ Failed to publish product pack. Please try again.');
                            console.error('Publish error:', error);
                          }
                        }
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md"
                    >
                      📤 Publish
                    </button>
                  )}
                  
                  {activePackData?.status === 'published' && (
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to unpublish this product pack? It will no longer be visible to users.')) {
                          try {
                                                        const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                              },
                              body: JSON.stringify({ status: 'draft' })
                            });
                            
                            if (response.ok) {
                              alert('✅ Product pack unpublished successfully!');
                              window.location.reload();
                            } else {
                              throw new Error('Failed to unpublish');
                            }
                          } catch (error) {
                            alert('❌ Failed to unpublish product pack. Please try again.');
                            console.error('Unpublish error:', error);
                          }
                        }
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition shadow-md"
                    >
                      📥 Unpublish
                    </button>
                  )}
                  
                  {(activePackData?.status === 'draft' || activePackData?.status === 'archived') && (
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to archive this product pack? Archived packs are hidden from lists.')) {
                          try {
                                                        const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                              },
                              body: JSON.stringify({ status: 'archived' })
                            });
                            
                            if (response.ok) {
                              alert('✅ Product pack archived successfully!');
                              window.location.reload();
                            } else {
                              throw new Error('Failed to archive');
                            }
                          } catch (error) {
                            alert('❌ Failed to archive product pack. Please try again.');
                            console.error('Archive error:', error);
                          }
                        }
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition shadow-md"
                    >
                      📦 Archive
                    </button>
                  )}
                  
                  {activePackData?.status === 'archived' && (
                    <button
                      onClick={async () => {
                        if (window.confirm('Do you want to restore this pack as Draft?')) {
                          try {
                                                        const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}`, {
                              method: 'PATCH',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                              },
                              body: JSON.stringify({ status: 'draft' })
                            });
                            
                            if (response.ok) {
                              alert('✅ Product pack restored to Draft!');
                              window.location.reload();
                            } else {
                              throw new Error('Failed to restore');
                            }
                          } catch (error) {
                            alert('❌ Failed to restore product pack. Please try again.');
                            console.error('Restore error:', error);
                          }
                        }
                      }}
                      className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md"
                    >
                      🔄 Restore to Draft
                    </button>
                  )}
                  
                  {/* Permanent Delete Button - Always visible with password protection */}
                  <button
                    onClick={async () => {
                      const password = prompt('⚠️ PERMANENT DELETE - This action cannot be undone!\n\nEnter password to confirm permanent deletion:');
                      
                      if (!password) {
                        return; // User cancelled
                      }
                      
                      if (password !== 'emzi4111') {
                        alert('❌ Invalid password. Permanent deletion cancelled.');
                        return;
                      }
                      
                      if (window.confirm(`⚠️ FINAL CONFIRMATION\n\nAre you absolutely sure you want to PERMANENTLY DELETE this product pack?\n\nPack ID: ${packId}\n\nThis action CANNOT be undone!`)) {
                        try {
                                                    const response = await fetch(`${BACKEND_URL}/api/v4/product-packs/${packId}/permanent-delete`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                            },
                            body: JSON.stringify({ password: password })
                          });
                          
                          if (response.ok) {
                            alert('✅ Product pack permanently deleted!');
                            // Redirect to pack list after deletion
                            window.location.href = '/knowledge-packs?type=product';
                          } else {
                            const error = await response.json();
                            throw new Error(error.detail || 'Failed to delete');
                          }
                        } catch (error) {
                          alert(`❌ Failed to permanently delete: ${error.message}`);
                          console.error('Permanent delete error:', error);
                        }
                      }
                    }}
                    className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-md border-2 border-red-800"
                  >
                    🗑️ Permanent Delete
                  </button>
                </div>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-1">Created</div>
                  <div className="text-sm text-gray-800">
                    {activePackData?.system?.created_by || 'Unknown'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {activePackData?.system?.created_at 
                      ? new Date(activePackData.system.created_at).toLocaleString()
                      : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-1">Last Updated</div>
                  <div className="text-sm text-gray-800">
                    {activePackData?.system?.updated_by || activePackData?.system?.created_by || 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {activePackData?.system?.updated_at 
                      ? new Date(activePackData.system.updated_at).toLocaleString()
                      : activePackData?.system?.created_at 
                        ? new Date(activePackData.system.created_at).toLocaleString()
                        : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Version History */}
              {activePackData?.system?.version_history && activePackData.system.version_history.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-800 mb-3">Version History</h4>
                  <div className="space-y-2">
                    {activePackData.system.version_history.map((version, i) => (
                      <div key={`item-${i}`} className="p-3 border border-gray-200 rounded-lg bg-white">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 text-xs font-bold bg-gray-200 text-gray-700 rounded">
                              v{version.version}
                            </span>
                            <span className="text-sm font-semibold text-gray-800">
                              {version.section_name}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(version.changed_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600">
                          {version.changes_summary}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          by {version.changed_by}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Audit Trail */}
              {packData?.system?.audit_trail && packData.system.audit_trail.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-800 mb-3">Audit Trail</h4>
                  <div className="max-h-60 overflow-y-auto space-y-1 p-3 bg-gray-50 rounded-lg">
                    {packData.system.audit_trail.map((entry, i) => (
                      <div key={`item-${i}`} className="text-xs text-gray-600 font-mono">
                        <span className="text-gray-500">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}
                        </span>
                        {' • '}
                        <span className="text-gray-700 font-semibold">{entry.action}</span>
                        {' • '}
                        <span className="text-gray-600">{entry.user}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!packData?.system?.version_history || packData.system.version_history.length === 0) &&
               (!packData?.system?.audit_trail || packData.system.audit_trail.length === 0) && (
                <p className="text-sm text-gray-500 italic">No version history or audit trail available yet</p>
              )}

              {/* Pack ID (for debugging/reference) */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  <span className="font-semibold">Pack ID:</span> {packData?.pack_id || packId || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
